/**
 * PROJ-060: Hook for fetching current session geolocation
 *
 * Used by AppFooter to display IP and country flag
 */

import { useQuery } from '@tanstack/react-query';

interface SessionGeoData {
  id: string;
  sessionId: string | null;
  ipAddress: string | null;
  ipMasked: string | null;
  countryCode: string | null;
  countryName: string | null;
  countryEmoji: string | null;
  city: string | null;
  regionCode: string | null;
  complianceRegion: string | null;
  isEu: boolean;
  sessionStartedAt: string | null;
  deviceType: string | null;
  browser: string | null;
  os: string | null;
  isActive: boolean;
}

interface SessionGeoResponse {
  success: boolean;
  data: SessionGeoData;
}

async function fetchCurrentSession(): Promise<SessionGeoData | null> {
  try {
    // Get auth token from localStorage (matching the app's auth pattern)
    const token = localStorage.getItem('accessToken');

    const response = await fetch('/api/v1/security-audit/current-session', {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data: SessionGeoResponse = await response.json();

    if (data?.success && data?.data) {
      return data.data;
    }

    return null;
  } catch (error) {
    console.error('Error fetching current session:', error);
    return null;
  }
}

export function useCurrentSession(refetchInterval = 60000) {
  return useQuery({
    queryKey: ['currentSession'],
    queryFn: fetchCurrentSession,
    refetchInterval,
    staleTime: 30000,
    retry: 1,
  });
}

export type { SessionGeoData };
